// src/resources/pipeline/hooks.ts
import { useMemo } from 'react';
import { useParams } from 'react-router-dom';

import { StepName, Query, Processes } from '../../design-system/types';
import { formatDossierOverview } from '../dossier/operations';
import { filterDossiersByQuery } from '../filters';
import { useGetArchivedDossierOverviews, useGetDossierOverviews } from '../dossier-overview/hooks';
import { useAuth } from '../../shared/providers/AuthProvider';
import { getProcessFromStepName } from '../../domain';
import { DossierId } from '../dossier/types';

const useDossierOverviewsByProcess = (process: Processes | undefined) => {
    const dossierOverviewsQuery = useGetDossierOverviews({
        enabled: process !== 'archive',
    });
    const archivedDossierOverviewsQuery = useGetArchivedDossierOverviews(undefined, {
        enabled: process === 'archive',
    });

    return process === 'archive' ? archivedDossierOverviewsQuery : dossierOverviewsQuery;
};

export const useDossierOverviewsFromUrlProcess = () => {
    const { processName } = useParams() as { processName: Processes | undefined };
    return useDossierOverviewsByProcess(processName);
};

export const useDossierOverviewFromId = (id: DossierId) => {
    const { data: dossierOverviews } = useDossierOverviewsFromUrlProcess();
    return useMemo(() => {
        return dossierOverviews?.[id];
    }, [dossierOverviews, id]);
};

const useDossierOverviewsByStepName = (name: StepName) => {
    const process = getProcessFromStepName(name);
    const { data: dossierOverviews } = useDossierOverviewsByProcess(process);

    return useMemo(() => {
        if (!dossierOverviews) return [];

        return Object.values(dossierOverviews)
            .filter((dossier) => dossier.step === name)
            .map((dossier) => formatDossierOverview({ dossierOverview: dossier }));
    }, [dossierOverviews, name]);
};

export const useDossierOverviewsMarkets = () => {
    const { data: dossierOverviews } = useDossierOverviewsFromUrlProcess();

    return useMemo(() => {
        if (!dossierOverviews) return [];

        return [
            ...new Set(
                Object.values(dossierOverviews).map((dossier) => dossier.project.marketCode),
            ),
            // eslint-disable-next-line sonarjs/no-alphabetical-sort -- market codes are short strings, alphabetical sort gives a stable UI order
        ].sort();
    }, [dossierOverviews]);
};

export const useHasMultipleMarkets = () => {
    const markets = useDossierOverviewsMarkets();
    return useMemo(() => markets.length > 1, [markets]);
};

export const useDossierOverviewsDestinations = () => {
    const { data: dossierOverviews } = useDossierOverviewsFromUrlProcess();

    return useMemo(() => {
        if (!dossierOverviews) return [];

        return [
            ...new Set(
                Object.values(dossierOverviews).map((dossier) => dossier.project.destinationLabel),
            ),
            // eslint-disable-next-line sonarjs/no-alphabetical-sort -- destination labels are strings, alphabetical sort gives a stable UI order
        ].sort();
    }, [dossierOverviews]);
};

export const useFilteredDossierOverviewsByProcess = (process: Processes, query: Query) => {
    const { data: dossierOverviews } = useDossierOverviewsByProcess(process);
    const { userId } = useAuth();

    return useMemo(() => {
        if (!dossierOverviews) return [];

        const formattedDossierOverviews = Object.values(dossierOverviews)
            .filter((dossier) => dossier.process === process)
            .map((dossier) => formatDossierOverview({ dossierOverview: dossier }));

        return filterDossiersByQuery(query, userId)(formattedDossierOverviews);
    }, [dossierOverviews, process, query, userId]);
};

export const useFilteredDossierOverviewsByStepName = (name: StepName, query: Query) => {
    const dossierOverviews = useDossierOverviewsByStepName(name);
    const { userId } = useAuth();

    return useMemo(
        () => filterDossiersByQuery(query, userId)(dossierOverviews),
        [dossierOverviews, query, userId],
    );
};
